import { TAppDispatch, TGetState } from 'store/types'
import { IBaseMediaItem, IMediaItem } from 'types/media'
import {
  getItemFromLocalStorage,
  setItemInLocalStorage
} from 'utils/localStorage'
import { RECENTLY_USED_ITEMS } from 'consts'
import { mergeRecentlyUsedItems } from 'utils/media'
import { userSelectors } from 'store/features/user/selectors'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import slice from 'store/features/recentlyUsedMedia/slice'
import { isBlobUrl } from 'utils'
import { filterValidImages } from 'utils/aiImage'

const actions = slice.actions

const updateRecentlyUsedItemsOnUpload =
  (uploadedItems: { id: string | number; filePath: string }[]) =>
  (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const userId = userSelectors.data(state)?.id
    const mediaItems = projectDataSelectors.mediaItems(state)
    const uploadedMediaItems: IMediaItem[] = mediaItems.filter(item =>
      uploadedItems.some(uploadedItem => uploadedItem.id === item.id)
    )

    const recentlyUsedItems: Record<number, IBaseMediaItem[]> =
      getItemFromLocalStorage(RECENTLY_USED_ITEMS) ?? {}

    if (recentlyUsedItems[userId]) {
      recentlyUsedItems[userId] = mergeRecentlyUsedItems(
        recentlyUsedItems[userId],
        uploadedMediaItems
      )
    } else {
      recentlyUsedItems[userId] = mergeRecentlyUsedItems([], uploadedMediaItems)
    }

    setItemInLocalStorage(RECENTLY_USED_ITEMS, recentlyUsedItems)

    dispatch(actions.setRecentlyUsedMedia(recentlyUsedItems[userId]))
  }

const updateRecentlyUsedItemsOnInsertFromMediaLibrary =
  (mediaItems: IMediaItem[]) =>
  (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const userId = userSelectors.data(state)?.id

    const recentlyUsedItems: Record<number, IBaseMediaItem[]> =
      getItemFromLocalStorage(RECENTLY_USED_ITEMS) ?? {}
    if (recentlyUsedItems?.[userId]) {
      recentlyUsedItems[userId] = mergeRecentlyUsedItems(
        recentlyUsedItems[userId],
        mediaItems
      )
    } else {
      recentlyUsedItems[userId] = mergeRecentlyUsedItems([], mediaItems)
    }

    setItemInLocalStorage(RECENTLY_USED_ITEMS, recentlyUsedItems)
    dispatch(actions.setRecentlyUsedMedia(recentlyUsedItems[userId]))
  }

const addNewItems =
  (mediaItems: IMediaItem[]) =>
  (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const userId = userSelectors.data(state)?.id

    const nonBlobMediaItems = mediaItems.filter(
      item => !item.meta.path || !isBlobUrl(item.meta.path)
    )

    if (nonBlobMediaItems.length === 0) {
      return
    }

    const recentlyUsedItems: Record<number, IBaseMediaItem[]> =
      getItemFromLocalStorage(RECENTLY_USED_ITEMS) ?? {}

    recentlyUsedItems[userId] = mergeRecentlyUsedItems(
      recentlyUsedItems[userId] ?? [],
      nonBlobMediaItems
    )

    setItemInLocalStorage(RECENTLY_USED_ITEMS, recentlyUsedItems)

    dispatch(actions.setRecentlyUsedMedia(recentlyUsedItems[userId]))
  }

const filterAndUpdateRecentlyUsedItems =
  () => async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const userId = userSelectors.data(state)?.id

    if (userId) {
      const items = getItemFromLocalStorage(RECENTLY_USED_ITEMS) ?? {}

      if (items?.[userId]) {
        const paths = items[userId].reduce(
          (acc: string[], item: IBaseMediaItem) => {
            if (item.path) {
              acc.push(item.path)
            }
            return acc
          },
          []
        )

        const validPaths = await filterValidImages(paths)

        const filteredItems = items[userId].filter(
          (item: IBaseMediaItem) => item.path && validPaths.includes(item.path)
        )

        if (filteredItems.length !== items[userId].length) {
          items[userId] = filteredItems
          setItemInLocalStorage(RECENTLY_USED_ITEMS, items)
        }

        dispatch(actions.replaceRecentlyUsedMedia(filteredItems))
      }
    }
  }

export const recentlyUsedMediaThunks = {
  ...actions,
  updateRecentlyUsedItemsOnUpload,
  updateRecentlyUsedItemsOnInsertFromMediaLibrary,
  filterAndUpdateRecentlyUsedItems,
  addNewItems
}
